import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import { Bot, Sparkles } from 'lucide-react';

interface AIAgentStepProps {
  onNext: (data: Record<string, any>) => void;
  data?: Record<string, any>;
  isLastStep?: boolean;
}

export const AIAgentStep: React.FC<AIAgentStepProps> = ({ onNext, data = {} }) => {
  const [agentName, setAgentName] = useState(data.agent_name || 'Assistente');
  const [personality, setPersonality] = useState(data.personality || 'helpful');
  const [customPersonality, setCustomPersonality] = useState(data.custom_personality || '');

  const handleContinue = () => {
    onNext({ 
      agent_name: agentName || 'Assistente',
      personality,
      custom_personality: personality === 'custom' ? customPersonality : undefined,
      configured: true 
    });
  };

  return (
    <Card className="border-none shadow-none">
      <CardHeader>
        <CardTitle>Configure seu Assistente de IA</CardTitle>
        <CardDescription>
          Personalize seu assistente virtual para atender seus clientes
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center gap-4 p-4 bg-primary/5 rounded-lg border border-primary/20">
          <Bot className="h-12 w-12 text-primary" />
          <div>
            <h3 className="font-medium text-lg">Assistente Virtual Inteligente</h3>
            <p className="text-sm text-muted-foreground">
              Seu assistente pessoal vai ajudar a automatizar atendimentos, responder perguntas frequentes e engajar seus clientes.
            </p>
          </div>
        </div>

        <div className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="agent_name">Nome do Assistente</Label>
            <Input
              id="agent_name"
              placeholder="Como seu assistente será chamado?"
              value={agentName}
              onChange={(e) => setAgentName(e.target.value)}
            />
            <p className="text-sm text-muted-foreground">
              Este nome será exibido para seus clientes durante as conversas.
            </p>
          </div>

          <div className="space-y-3">
            <Label>Personalidade do Assistente</Label>
            <RadioGroup value={personality} onValueChange={setPersonality} className="space-y-3">
              <div className="flex items-center space-x-2 rounded-md border p-3 hover:bg-accent">
                <RadioGroupItem value="helpful" id="helpful" />
                <Label htmlFor="helpful" className="flex-1 cursor-pointer font-normal">
                  <span className="font-medium">Prestativo</span>
                  <p className="text-sm text-muted-foreground">
                    Sempre busca ajudar e resolver problemas de forma eficiente.
                  </p>
                </Label>
              </div>
              
              <div className="flex items-center space-x-2 rounded-md border p-3 hover:bg-accent">
                <RadioGroupItem value="friendly" id="friendly" />
                <Label htmlFor="friendly" className="flex-1 cursor-pointer font-normal">
                  <span className="font-medium">Amigável</span>
                  <p className="text-sm text-muted-foreground">
                    Conversa de forma calorosa e descontraída, criando conexão.
                  </p>
                </Label>
              </div>
              
              <div className="flex items-center space-x-2 rounded-md border p-3 hover:bg-accent">
                <RadioGroupItem value="professional" id="professional" />
                <Label htmlFor="professional" className="flex-1 cursor-pointer font-normal">
                  <span className="font-medium">Profissional</span>
                  <p className="text-sm text-muted-foreground">
                    Formal e direto, mantém um tom corporativo adequado.
                  </p>
                </Label>
              </div>
              
              <div className="flex items-center space-x-2 rounded-md border p-3 hover:bg-accent">
                <RadioGroupItem value="custom" id="custom" />
                <Label htmlFor="custom" className="flex-1 cursor-pointer font-normal">
                  <span className="font-medium">Personalizado</span>
                  <p className="text-sm text-muted-foreground">
                    Defina uma personalidade específica para seu assistente.
                  </p>
                </Label>
              </div>
            </RadioGroup>
          </div>
          
          {personality === 'custom' && (
            <div className="space-y-2">
              <Label htmlFor="custom_personality">Descrição da Personalidade</Label>
              <Textarea
                id="custom_personality"
                placeholder="Descreva como seu assistente deve interagir..."
                value={customPersonality}
                onChange={(e) => setCustomPersonality(e.target.value)}
                rows={3}
              />
            </div>
          )}
        </div>
        
        <div className="flex items-center gap-2 p-3 bg-orange-50 rounded-lg border border-orange-200">
          <Sparkles className="h-5 w-5 text-orange-500" />
          <p className="text-sm text-orange-800">
            Você poderá personalizar mais detalhes do seu assistente posteriormente no painel de controle.
          </p>
        </div>
      </CardContent>
      <CardFooter className="flex justify-between">
        <Button variant="outline" onClick={() => window.history.back()}>
          Voltar
        </Button>
        <Button onClick={handleContinue}>
          Continuar
        </Button>
      </CardFooter>
    </Card>
  );
}; 